/**
 * AutoMovementSettings — movimentação automática.
 * Mobile: toolbar Contatos-like. Desktop intacto.
 */
import React, { useState, useMemo, useEffect } from 'react';
import SearchInput from '../../../components/ui/SearchInput';
import { Button } from '../../../components/ui/Button';
import ActionMenu from '../../../components/shared/ActionMenu';
import BulkActionsBar from '../../../components/shared/BulkActionsBar';
import Pagination from '../../../components/shared/Pagination';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import AutoMovementTable from './components/AutoMovementTable';
import AutoMovementFormPanel from './components/AutoMovementFormPanel';
import { useAutoMovementPage } from './hooks/useAutoMovementPage';
import { PageHeader } from '../../../components/layout/Header';

const AutoMovementSettings: React.FC = () => {
    const {
        filteredMovements,
        isLoading,
        searchTerm,
        setSearchTerm,
        selectedIds,
        isPanelOpen,
        movementToEdit,
        handleOpenCreatePanel,
        handleOpenEditPanel,
        handleClosePanel,
        handleSave,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        movementToDelete,
        handleOpenDeleteModal,
        handleConfirmDelete,
        isBulkDeleteModalOpen,
        setIsBulkDeleteModalOpen,
        handleBulkDelete,
        handleDuplicate,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        headerActions,
        bulkActions,
    } = useAutoMovementPage();

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const paginatedMovements = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * pageSize;
        return filteredMovements.slice(firstPageIndex, firstPageIndex + pageSize);
    }, [currentPage, filteredMovements, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    return (
        <div className="flex h-full flex-col">
            <PageHeader
                title="Movimentação Automática"
                subtitle="Automação precisa para decisões que acontecem no tempo certo"
                className="mb-0 gap-s3 md:mb-6 md:gap-3"
                titleClassName="text-lg md:text-xl"
                actionClassName="w-full md:w-auto"
                hideBackButton
                action={
                    <>
                        <div className="flex w-full flex-col gap-s3 md:hidden">
                            <div
                                role="toolbar"
                                aria-label="Ações de movimentação"
                                className="inline-flex h-11 w-fit items-stretch overflow-hidden rounded-xl border border-border bg-card"
                            >
                                <ActionMenu
                                    actions={headerActions}
                                    buttonClassName="h-11 w-11 rounded-none border-0 bg-transparent p-0 text-default shadow-none hover:bg-surface-subtle hover:text-primary"
                                />
                            </div>
                            <SearchInput
                                value={searchTerm}
                                onChange={setSearchTerm}
                                placeholder="Pesquisar movimentações..."
                                className="min-h-11 w-full"
                            />
                            <Button
                                type="button"
                                onClick={handleOpenCreatePanel}
                                className="min-h-11 w-full touch-manipulation"
                            >
                                Nova Movimentação
                            </Button>
                        </div>
                        <div className="hidden md:flex md:items-center md:gap-2.5">
                            <div className="w-[250px] shrink-0">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Pesquisar movimentações..."
                                    className="h-[36px]"
                                />
                            </div>
                            <Button type="button" onClick={handleOpenCreatePanel}>
                                Nova Movimentação
                            </Button>
                            <ActionMenu actions={headerActions} />
                        </div>
                    </>
                }
            />

            <section aria-label="Lista de movimentações" className="overflow-x-auto pt-s3 md:pt-0">
                <AutoMovementTable
                    movements={paginatedMovements}
                    onEdit={handleOpenEditPanel}
                    onDelete={handleOpenDeleteModal}
                    onDuplicate={handleDuplicate}
                    selectedIds={selectedIds}
                    onToggleSelection={toggleSelection}
                    onToggleAll={() => toggleAllSelection(filteredMovements)}
                />
            </section>
            <Pagination
                className="mt-s3"
                currentPage={currentPage}
                totalCount={filteredMovements.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                selectedCount={selectedIds.size}
            />

            <AutoMovementFormPanel
                isOpen={isPanelOpen}
                onClose={handleClosePanel}
                onSave={handleSave}
                movementToEdit={movementToEdit}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Excluir Movimentação"
                message={`Tem certeza que deseja excluir a movimentação "${movementToDelete?.message}"?`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ConfirmationModal
                isOpen={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                onConfirm={handleBulkDelete}
                title="Excluir Movimentações"
                message={`Tem certeza que deseja excluir ${selectedIds.size} movimentação(ões)? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <BulkActionsBar
                selectedCount={selectedIds.size}
                actions={bulkActions}
                onClose={clearSelection}
            />
        </div>
    );
};

export default AutoMovementSettings;
